import "react-image-crop/dist/ReactCrop.css";

import { CropIcon } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Subtitle, Title } from "../Text";
import { Button } from "../ui/button";
import { CustomDialogFooter } from "../ui/custom-dialog";
import { DialogHeader } from "../ui/dialog";
import { useMemo } from "react";
import { useRef, useState } from "react";
import ReactCrop, { type Crop } from "react-image-crop";
import { getCroppedImgAsFile } from "@/lib/canvas";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "../ui/form";
import { Thumbnail } from "../ui/tumbnail";

const formSchema = z.object({
  // Optional: if the user never draws a crop, we fall back to the full image.
  crop: z.custom<Crop>().optional(),
});

export const ArtmatchCropImage = ({
  baseImage,
  onCancel,
  onNextStep,
}: {
  onCancel: () => void;
  onNextStep: (file: File) => void;
  baseImage: File;
}) => {
  const { t } = useTranslation();

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
  });

  const baseImageUrl = useMemo(() => {
    return URL.createObjectURL(baseImage);
  }, [baseImage]);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const [displayedSize, setDisplayedSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  function fullImageCrop(width: number, height: number): Crop {
    return { x: 0, y: 0, width, height, unit: "px" };
  }

  function handleImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const target = e.currentTarget;
    const size = { width: target.width, height: target.height };
    setDisplayedSize(size);
    imgRef.current = target;
    // Default to the full frame so submit always hits the API.
    if (!form.getValues("crop")) {
      form.setValue("crop", fullImageCrop(size.width, size.height), {
        shouldValidate: true,
      });
    }
  }

  function initCrop() {
    if (!displayedSize) return;
    form.setValue("crop", fullImageCrop(displayedSize.width, displayedSize.height), {
      shouldValidate: true,
    });
  }

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (!displayedSize) return;

    const crop =
      values.crop ??
      ({
        x: 0,
        y: 0,
        width: displayedSize.width,
        height: displayedSize.height,
        unit: "px",
      } as Crop);

    const coverage =
      (crop.width * crop.height) / (displayedSize.width * displayedSize.height);
    const isFullFrame =
      coverage >= 0.98 &&
      Math.abs(crop.x) < 2 &&
      Math.abs(crop.y) < 2 &&
      Math.abs(crop.width - displayedSize.width) < 4 &&
      Math.abs(crop.height - displayedSize.height) < 4;

    if (isFullFrame) {
      onNextStep(baseImage);
      return;
    }

    const croppedImage = await getCroppedImgAsFile(
      baseImageUrl,
      crop,
      displayedSize,
      0,
    );
    onNextStep(croppedImage);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogHeader className="px-6 pt-6">
          <div className="flex gap-4 items-center">
            <Thumbnail
              onClick={initCrop}
              className="cursor-pointer hover:bg-gray-50"
            >
              <CropIcon className="text-gray-700 size-6" />
            </Thumbnail>
            <div className="flex flex-col w-full">
              <Title>{t("artmatch.cropImage.title")}</Title>
              <Subtitle>{t("artmatch.cropImage.subtitle")}</Subtitle>
            </div>
          </div>
        </DialogHeader>
        <div className="p-6">
          <Controller
            control={form.control}
            name="crop"
            render={({ field }) => (
              <ReactCrop
                onDragEnd={() => field.onBlur()}
                className="h-fit max-h-[512px]"
                crop={field.value}
                onChange={(c) => field.onChange(c)}
              >
                <img src={baseImageUrl} onLoad={handleImageLoad} />
              </ReactCrop>
            )}
          />
        </div>
        <CustomDialogFooter>
          <Button type="button" onClick={onCancel} variant="secondary">
            {t("common.cancel")}
          </Button>
          <Button type="submit">{t("artmatch.cropImage.submit")}</Button>
        </CustomDialogFooter>
      </form>
    </Form>
  );
};
